import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  useTheme,
  FormControl,
  InputLabel,
  Select
} from "@mui/material";
import { LineChart } from "@mui/x-charts";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import GetAppIcon from "@mui/icons-material/GetApp";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useGetStaffShipmentStatsQuery } from "store/services/dashboardApi";

const TIME_PERIODS = [
  { value: 'week', label: 'Last 7 Days' },
  { value: 'month', label: 'Last 30 Days' },
  { value: 'year', label: 'Last 12 Months' }
];

const ShipmentVendorStaffLineChart = () => {
  const [timePeriod, setTimePeriod] = useState('week');
  const { data: shipmentStats = {}, isLoading, isError } = useGetStaffShipmentStatsQuery({ period: timePeriod });

  const createdShipmentsData = shipmentStats.createdShipments || [];
  const deliveredShipmentsData = shipmentStats.deliveredShipments || [];

  const chartRef = useRef(null);
  const theme = useTheme();
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const menuTimeout = useRef(null);

  const handleTimePeriodChange = (event) => {
    setTimePeriod(event.target.value);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
    clearTimeout(menuTimeout.current);
    menuTimeout.current = setTimeout(() => {
      setAnchorEl(null);
    }, 3000);
  };

  const handleMenuClose = () => {
    clearTimeout(menuTimeout.current);
    setAnchorEl(null);
  };

  useEffect(() => {
    if (open) {
      clearTimeout(menuTimeout.current);
      menuTimeout.current = setTimeout(() => {
        setAnchorEl(null);
      }, 5000);
    }
    return () => clearTimeout(menuTimeout.current);
  }, [open]);

  const handleFullScreen = () => {
    if (chartRef.current?.requestFullscreen) {
      chartRef.current.requestFullscreen();
    }
    setIsFullScreen(true);
    handleMenuClose();
  };

  const handleMinimize = () => {
    document.exitFullscreen();
    setIsFullScreen(false);
  };

  useEffect(() => {
    const exitHandler = () => {
      if (!document.fullscreenElement) {
        setIsFullScreen(false);
      }
    };
    document.addEventListener("fullscreenchange", exitHandler);
    return () => document.removeEventListener("fullscreenchange", exitHandler);
  }, []);

  const handleDownload = async (format) => {
    if (!chartRef.current) return;

    const canvas = await html2canvas(chartRef.current);
    const image = canvas.toDataURL("image/png");

    if (format === "pdf") {
      const pdf = new jsPDF();
      pdf.addImage(image, "PNG", 10, 10, 180, 160);
      pdf.save("shipment_chart.pdf");
    } else {
      const link = document.createElement("a");
      link.href = image;
      link.download = `shipment_chart.${format}`;
      link.click();
    }
  };

  // Helper functions for generating date range and keys
  const generateDateRange = (period) => {
    const today = new Date();
    const dates = [];

    if (period === 'week') {
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        dates.push(date);
      }
    } else if (period === 'month') {
      for (let i = 29; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        dates.push(date);
      }
    } else if (period === 'year') {
      const currentYear = today.getFullYear();
      const currentMonth = today.getMonth();
      for (let i = 11; i >= 0; i--) {
        const month = currentMonth - i;
        const year = currentYear + Math.floor(month / 12);
        const adjustedMonth = (month + 12) % 12;
        dates.push(new Date(year, adjustedMonth, 1));
      }
    }

    return dates;
  };

  const getDateKey = (date, period) => {
    if (period === 'year') {
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    }
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  // Format dates based on selected time period
  const formatDate = (dateString, period) => {
    const date = new Date(dateString);
    if (period === 'year') {
      return date.toLocaleDateString("en-US", { month: "short" });
    } else if (period === 'month') {
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    } else {
      return date.toLocaleDateString("en-US", { weekday: "short", day: "numeric" });
    }
  };

  // Generate complete date range
  const allDates = generateDateRange(timePeriod);
  const dateLabels = allDates.map(date => formatDate(date, timePeriod));

  // Create lookup maps for API data
  const createdShipmentsMap = new Map();
  createdShipmentsData.forEach(entry => {
    const date = new Date(entry.date);
    const key = getDateKey(date, timePeriod);
    createdShipmentsMap.set(key, entry.count);
  });

  const deliveredShipmentsMap = new Map();
  deliveredShipmentsData.forEach(entry => {
    const date = new Date(entry.date);
    const key = getDateKey(date, timePeriod); 
    deliveredShipmentsMap.set(key, entry.count);
  });

  // Map to complete date range
  const createdCounts = allDates.map(date => {
    return createdShipmentsMap.get(getDateKey(date, timePeriod)) || 0;
  });

  const deliveredCounts = allDates.map(date => {
    return deliveredShipmentsMap.get(getDateKey(date, timePeriod)) || 0;
  });

  if (isLoading) return <div>Loading chart...</div>;
  if (isError) return <div>Error loading data</div>;

  return (
    <Box
      ref={chartRef}
      sx={{
        mt: 2,
        width: isFullScreen ? "100vw" : "100%",
        height: isFullScreen ? "100vh" : "auto",
        minHeight: isFullScreen ? "100vh" : "auto",
        padding: 2,
        boxShadow: 2,
        borderRadius: 2,
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        position: "relative",
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="p" sx={{ fontWeight: "bold", color: theme.palette.primary.main }}>
          Shipment Trends
        </Typography>
        <Box display="flex" alignItems="center">
          <FormControl size="small" sx={{ minWidth: 120, mr: 2 }}>
            <InputLabel>Time Period</InputLabel>
            <Select
              value={timePeriod}
              onChange={handleTimePeriodChange}
              label="Time Period"
            >
              {TIME_PERIODS.map((period) => (
                <MenuItem key={period.value} value={period.value}>
                  {period.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box>
            {isFullScreen && (
              <IconButton onClick={handleMinimize}>
                <FullscreenExitIcon />
              </IconButton>
            )}
            <IconButton onClick={handleMenuClick}>
              <MoreVertIcon />
            </IconButton>
            <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
              <MenuItem onClick={handleFullScreen}>View Full Screen</MenuItem>
              <MenuItem onClick={() => handleDownload("png")}>
                <GetAppIcon sx={{ mr: 1 }} /> Download PNG
              </MenuItem>
              <MenuItem onClick={() => handleDownload("jpeg")}>
                <GetAppIcon sx={{ mr: 1 }} /> Download JPEG
              </MenuItem>
              <MenuItem onClick={() => handleDownload("pdf")}>
                <PictureAsPdfIcon sx={{ mr: 1 }} /> Download PDF
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Box>

      <Grid sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
        <LineChart
          xAxis={[{
            scaleType: "point",
            data: dateLabels,
            label: timePeriod === 'year' ? 'Month' : 'Date',
          }]}
          yAxis={[{
            min: 0,
            max: Math.max(...createdCounts, ...deliveredCounts, 10),
          }]}
          series={[
            {
              data: createdCounts,
              label: "Created",
              color: theme.palette.primary.main,
              strokeWidth: 3
            },
            {
              data: deliveredCounts,
              label: "Delivered",
              color: theme.palette.success.main,
              strokeWidth: 3
            }
          ]}
          width={timePeriod === 'month' ? (isFullScreen ? 1000 : 1000) : (isFullScreen ? 1000 : 600)}
          height={isFullScreen ? 700 : 300}
        />
      </Grid>
    </Box>
  );
};

export default ShipmentVendorStaffLineChart;
